import { useCallback, useEffect, useRef, useState } from "react";
import Canvas from "./Canvas";
import Menu from "./Menu";
import MobileMenu from "./MobileMenu";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { SlBasket } from "react-icons/sl";
import useIsConnected from "../../utils/IsConnected";
import { Popover, Spin, Tooltip } from "antd";
import { GlobalOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../apis/store";
import { FaSignOutAlt, FaUser } from "react-icons/fa";
import { FaCarRear } from "react-icons/fa6";
import Cart from "./Cart";
import LanguageSwitchModal from "./LanguageSwitchModal";
import { useTranslation } from "react-i18next";
import { useCookies } from "react-cookie";
import { resetState } from "../../apis/slices/globalSlice";
import { toGetProfil } from "../../apis/actions/auth.actions";
import { toSyncMyCartNow } from "../../apis/actions/cart.actions";
import { showLanguageSwitchSplashBeforeReload } from "../../utils/languageSwitchSplash";
import { getCartItemCount } from "../../utils/cartCount";
import { CART_ADDED_RESULT_EVENT, type CartAddedResult } from "../../utils/cartAddedFeedback";
import { confirmAction } from "../../utils/Utils";

interface Props {
  type?: "internal" | "external";
}

const Navbar = ({ type }: Props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [, , removeCookie] = useCookies(["apiKey"]);
  const isConnected = useIsConnected();
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [cartOpen, setIsCartOpen] = useState(false);
  const [langModalOpen, setLangModalOpen] = useState(false);
  const [cartBadgeBump, setCartBadgeBump] = useState(false);
  const [cartCelebrate, setCartCelebrate] = useState(false);
  const { services, products, cartReady, cartError } = useSelector(
    (state: RootState) => state?.global
  );
  const addShopLoading = useSelector(
    (state: RootState) => state?.services?.AddShop?.loading ?? false
  );
  const skipFirstCartBump = useRef(true);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const { t, i18n } = useTranslation();
  const [language, setLanguage] = useState<string>(() => {
    const raw = localStorage.getItem("lang") || i18n.language || "en";
    return raw.toLowerCase().startsWith("ar") ? "ar" : "en";
  });

  const handleLanguageChange = (lang: "en" | "ar") => {
    const next = lang.split("-")[0] as "en" | "ar";
    const current = (i18n.language || "en").split("-")[0];
    if (next === current) return;
    showLanguageSwitchSplashBeforeReload(next);
    setLanguage(next);
    window.location.reload();
  };

  useEffect(() => {
    const normalized = language.toLowerCase().startsWith("ar") ? "ar" : "en";
    try {
      localStorage.setItem("lang", normalized);
    } catch {
      /* ignore private mode */
    }
    document.documentElement.dir = normalized === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = normalized;
    void i18n.changeLanguage(normalized);
  }, [language, i18n]);

  const [tooltipCar, setTooltipCar] = useState(false);

  const cartCount = isConnected && (!cartReady || cartError) ? 0 : getCartItemCount(services, products);
  const cartOverflowMax = 99;
  const cartBadgeText =
    cartCount > cartOverflowMax ? `${cartOverflowMax}+` : String(cartCount);
  const canOpenCart =
    location.pathname !== "/order" && Boolean(isConnected);

  useEffect(() => {
    let celebrateId: number | undefined;
    const onResult = (event: Event) => {
      if ((event as CustomEvent<CartAddedResult>).detail.success) {
        if (celebrateId !== undefined) window.clearTimeout(celebrateId);
        setCartCelebrate(true);
        celebrateId = window.setTimeout(() => setCartCelebrate(false), 900);
      }
    };
    window.addEventListener(CART_ADDED_RESULT_EVENT, onResult);
    return () => {
      window.removeEventListener(CART_ADDED_RESULT_EVENT, onResult);
      if (celebrateId !== undefined) window.clearTimeout(celebrateId);
    };
  }, []);

  useEffect(() => {
    if (skipFirstCartBump.current) {
      skipFirstCartBump.current = false;
      return;
    }
    setCartBadgeBump(true);
    const id = window.setTimeout(() => setCartBadgeBump(false), 420);
    return () => window.clearTimeout(id);
  }, [cartCount]);

  const openCart = () => {
    if (!canOpenCart) return;
    setIsCartOpen(true);
  };

  const handleLogout = useCallback(async () => {
    const confirmed = await confirmAction({
      title: t("profile.logoutConfirm.title"),
      text: t("profile.logoutConfirm.text"),
      confirmText: t("profile.logoutConfirm.confirm"),
      cancelText: t("profile.logoutConfirm.cancel"),
    });
    if (!confirmed) return;

    await dispatch(toSyncMyCartNow());
    removeCookie("apiKey", { path: "/" });
    dispatch(resetState());
    dispatch(toGetProfil(i18n.language));
    navigate("/");
    dispatch(resetState());
  }, [dispatch, i18n.language, navigate, removeCookie, t]);

  const isInternal = type === "internal";
  /**
   * Single navbar: internal pages never use theme `.stick` (fixed + animated jump).
   * Scroll state is `.ac-nav--scrolled` with sticky positioning in SCSS.
   * Home still uses `.stick` when scrolled (overlay hero → fixed bar).
   */
  const homeScrolledStick = !isInternal && isScrolled;
  const internalScrolled = isInternal && isScrolled;

  const currentLang = ((i18n.language || "en").split("-")[0] === "ar"
    ? "ar"
    : "en") as "en" | "ar";

  const cartPopoverTitle = (
    <span className="ac-nav__cart-popover-title">
      <InfoCircleOutlined
        className="ac-nav__cart-popover-info-icon"
        aria-hidden
      />
      {t("services.cartIcon.tooltipTitle")}
    </span>
  );

  const cartPopoverContent = (
    <div className="ac-nav__cart-popover-body">
      <p className="ac-nav__cart-popover-line ac-nav__cart-popover-line--strong">
        {cartCount > 0
          ? t("services.cartIcon.itemsLine", { count: cartCount })
          : t("services.cartIcon.emptyHeadline")}
      </p>
      <p className="ac-nav__cart-popover-line ac-nav__cart-popover-line--muted">
        {cartCount > 0
          ? t("services.cartIcon.hintItems")
          : t("services.cartIcon.hintEmpty")}
      </p>
    </div>
  );

  return (
    <>
      <header
        className={`header sticky-bar header-home-2 border-0 ac-nav${
          homeScrolledStick ? " stick" : ""
        }${internalScrolled ? " ac-nav--scrolled" : ""}${
          !isInternal && !isScrolled ? " ac--fixed" : ""
        }${!isInternal ? " ac-nav--home" : ""}`}
      >
        <div
          className={`${
            i18n.language.startsWith("ar")
              ? "container-fluid-rtl"
              : "container-fluid"
          } ac-nav__inner background-body ${
            isInternal || isScrolled ? "ac--background-light" : "bg-transparent"
          }`}
        >
          <div className="main-header ac-nav__main">
            <div className="header-left">
              <div className="header-logo">
                <Link className="d-flex align-items-center" to="/">
                  <img alt="" src="assets/imgs/template/logo-w.svg" />
                </Link>
              </div>
              <Menu type={type} />
            </div>
            <div className="header-right ac-nav__actions">
              <div
                className="ac-nav__cluster"
                role="toolbar"
                aria-label={t("menu.actionToolbar")}
                dir={i18n.language.startsWith("ar") ? "rtl" : "ltr"}
              >
                <div className="ac-nav__cluster-seg">
                  <Tooltip
                    title={
                      isConnected ? t("profile.profil") : t("login.enter")
                    }
                    overlayClassName="ac--share-tooltip"
                  >
                    <Link
                      className="ac-nav__cluster-btn ac-nav__cluster-btn--link"
                      to={isConnected ? "/profile" : "/login"}
                      aria-label={
                        isConnected ? t("profile.profil") : t("login.enter")
                      }
                    >
                      <FaUser size={19} className="flex-shrink-0" />
                    </Link>
                  </Tooltip>
                </div>

                <div className="ac-nav__cluster-seg">
                  <button
                    type="button"
                    className="ac-nav__cluster-btn"
                    aria-label={`${t("menu.language")}: ${language.toUpperCase()}`}
                    aria-haspopup="dialog"
                    aria-expanded={langModalOpen}
                    onClick={() => setLangModalOpen(true)}
                  >
                    <GlobalOutlined className="ac-nav__cluster-icon-globe" />
                  </button>
                </div>

                {isConnected ? (
                  <div className="ac-nav__cluster-seg">
                    <Tooltip
                      title={t("profile.cars")}
                      overlayClassName="ac--share-tooltip"
                      open={tooltipCar}
                      onOpenChange={setTooltipCar}
                    >
                      <button
                        type="button"
                        className="ac-nav__cluster-btn"
                        onClick={() => {
                          setTooltipCar(false);
                          setVisible((v) => !v);
                        }}
                        aria-label={t("profile.cars")}
                      >
                        <FaCarRear
                        size={20}
                        className={
                          i18n.language.startsWith("ar")
                            ? "flex-shrink-0 ac-nav__cluster-icon-mirror"
                            : "flex-shrink-0"
                        }
                      />
                      </button>
                    </Tooltip>
                  </div>
                ) : null}

                {isConnected ? (
                  <div className="ac-nav__cluster-seg">
                    <Tooltip
                      title={t("profile.logout")}
                      overlayClassName="ac--share-tooltip"
                    >
                      <button
                        type="button"
                        className="ac-nav__cluster-btn ac-nav__cluster-btn--logout"
                        onClick={handleLogout}
                        aria-label={t("profile.logout")}
                      >
                        <FaSignOutAlt
                          size={19}
                          className={
                            i18n.language.startsWith("ar")
                              ? "flex-shrink-0 ac-nav__cluster-icon-mirror"
                              : "flex-shrink-0"
                          }
                        />
                      </button>
                    </Tooltip>
                  </div>
                ) : null}

                <div className="ac-nav__cluster-seg ac-nav__cluster-seg--cart">
                  <Popover
                    title={cartPopoverTitle}
                    content={cartPopoverContent}
                    trigger={["hover", "focus"]}
                    placement="bottom"
                    mouseEnterDelay={0.12}
                    rootClassName="ac-nav__cart-popover"
                  >
                    <button
                      type="button"
                      className={`ac-nav__cluster-btn ac-nav__cluster-btn--cart${
                        cartCelebrate ? " ac-nav__cluster-btn--cart-celebrate" : ""
                      }${cartBadgeBump ? " ac-nav__cluster-btn--cart-bump" : ""}`}
                      onClick={() => {
                        openCart();
                      }}
                      aria-label={
                        cartCount > 0
                          ? `${t("profile.car.cart")} (${cartBadgeText})`
                          : t("profile.car.cart")
                      }
                    >
                      {/*
                        Custom count pill (no Ant Badge): Ant’s translate(50%,-50%) ties position
                        to pill width, so the number jumps when 9→10 and overrides often lose to css-in-js.
                      */}
                      <span
                        className={`ac-nav__cart-root${
                          cartBadgeBump ? " ac-nav__cart-root--bump" : ""
                        }`}
                      >
                        <span
                          className={`ac-nav__cart-icon-slot${
                            addShopLoading ? " ac-nav__cart-icon-slot--syncing" : ""
                          }`}
                          aria-busy={addShopLoading}
                        >
                          {addShopLoading ? (
                            <Spin size="small" className="ac-nav__cart-sync-spin" />
                          ) : (
                            <SlBasket size={21} />
                          )}
                        </span>
                        {cartCount > 0 ? (
                          <span
                            className="ac-nav__cart-count-pill"
                            aria-hidden
                          >
                            {cartBadgeText}
                          </span>
                        ) : null}
                      </span>
                    </button>
                  </Popover>
                </div>
              </div>

                <button
                  type="button"
                  className="burger-icon burger-icon-white ac-nav__burger"
                  onClick={() => setOpen(true)}
                  aria-label={t("menu.openMenu")}
                >
                  <span className="burger-icon-top" />
                  <span className="burger-icon-mid"> </span>
                  <span className="burger-icon-bottom"> </span>
                </button>
              </div>
          </div>
        </div>
      </header>
      <LanguageSwitchModal
        open={langModalOpen}
        onClose={() => setLangModalOpen(false)}
        current={currentLang}
        onPick={(lang) => {
          setLangModalOpen(false);
          handleLanguageChange(lang);
        }}
      />
      <MobileMenu open={open} setOpen={setOpen} />
      <Canvas visible={visible} setVisible={(v) => setVisible(v)} />
      <Cart visible={cartOpen} setVisible={(v) => setIsCartOpen(v)} />
    </>
  );
};

export default Navbar;
